'use client';

import React, { Fragment, useEffect, useState, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';

import Load from '../../components/Load';
import {
    LIGAS_FILTRADAS,
    LigasFiltradasData,
    LigasFiltradasVars,
} from '../../services/ligaService';
import { useDebounce } from '../../hooks/useDebounce';

import Header from '../../components/Header';
import LeagueCard from '../../components/LeagueCard';
import LeagueFilterModal, { LeagueFilters } from '../../components/LeagueFilterModal';

const PAGE_SIZE = 10;

const LeaguesPage: React.FC = () => {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 400);
    const [page, setPage] = useState(0);
    const [filterOpen, setFilterOpen] = useState(false);
    const filterBtnRef = useRef<HTMLButtonElement>(null);
    const [filters, setFilters] = useState<LeagueFilters>({});

    const [fetchFiltered, { data: filteredData, loading: loadingFiltered, error }] = useLazyQuery<
        LigasFiltradasData,
        LigasFiltradasVars
    >(LIGAS_FILTRADAS, { fetchPolicy: 'no-cache' });

    useEffect(() => {
        fetchFiltered({
            variables: {
                nome: debouncedSearch || filters.nome || null,
                username: filters.username || null,
                iuguConfigId: null,
                direction: 'ASC',
                page,
                size: PAGE_SIZE,
                sortBy: 'nome',
            },
        });
    }, [debouncedSearch, page, filters, fetchFiltered]);

    const ligas = filteredData?.ligasFiltradas.items || [];

    return (
        <Fragment>
            <Header />

            <div className="main-content right-chat-active">
                <div className="middle-sidebar-bottom">
                    <div className="middle-sidebar-left pe-0">
                        <div className="row">
                            <div className="col-xl-12">
                                <div className="d-flex align-items-center justify-content-between bg-white p-4 rounded-3 mb-4">
                                    <h2 className="fw-700 mb-0">Ligas</h2>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="position-relative" style={{ maxWidth: 250 }}>
                                            <input
                                                id="search-league"
                                                type="text"
                                                value={search}
                                                onChange={(e) => {
                                                    setPage(0);
                                                    setSearch(e.target.value);
                                                }}
                                                className="form-control pe-5 bg-lightgrey"
                                                placeholder="Buscar aqui."
                                                aria-label="Buscar ligas"
                                                style={{ paddingRight: 40 }}
                                            />
                                            <span
                                                className="position-absolute top-50 end-0 translate-middle-y me-3"
                                                style={{ pointerEvents: 'none' }}
                                            >
                                                <i className="bi bi-search" style={{ fontSize: 18, color: '#888' }}></i>
                                            </span>
                                        </div>
                                        <button
                                            id='filter-chain'
                                            ref={filterBtnRef}
                                            className="btn bg-lightgrey d-flex align-items-center"
                                            style={{ height: 48 }}
                                            onClick={() => setFilterOpen(true)}
                                        >
                                            <i className="bi bi-filter font-xxl"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {loadingFiltered && (
                                <div className="col-12">
                                    <Load />
                                </div>
                            )}
                            {error && (
                                <div className="col-12">
                                    <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
                                        <div className="card-body text-center">
                                            <p className="text-danger mb-0">Erro ao carregar ligas.</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!loadingFiltered && !error && ligas.length === 0 && (
                                <div className="col-12">
                                    <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
                                        <div className="card-body text-center">
                                            <p className="mb-0">Nenhuma liga encontrada.</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {ligas.map((liga) => (
                                <LeagueCard key={liga.id} liga={liga} />
                            ))}
                            {filteredData?.ligasFiltradas && (
                                <div className="col-12">
                                    <div className="d-flex justify-content-center my-3 gap-2">
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                                            disabled={(filteredData?.ligasFiltradas?.page ?? 0) <= 0}
                                        >
                                            Anterior
                                        </button>
                                        {Array.from({ length: filteredData.ligasFiltradas.totalPages }, (_, i) => (
                                            <button
                                                key={i}
                                                className={`btn ${i === filteredData.ligasFiltradas.page ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                onClick={() => setPage(i)}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => setPage((p) => p + 1)}
                                            disabled={
                                                (filteredData?.ligasFiltradas?.page ?? 0) >=
                                                ((filteredData?.ligasFiltradas?.totalPages ?? 1) - 1)
                                            }
                                        >
                                            Próxima
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <LeagueFilterModal
                open={filterOpen}
                onClose={() => {
                    setFilterOpen(false);
                    filterBtnRef.current?.focus();
                }}
                onFilter={(f) => {
                    setPage(0);
                    setFilters(f);
                }}
            />
        </Fragment>
    );
};

export default LeaguesPage;
